import React, { useCallback, useContext, useMemo, useState } from "react";
import { Stack, Box, useTheme } from "@mui/material";
import LeadSidebar from "@/modules/lead-page/new/components/sidebar/lead-sidebar";
import CustomerSidebarSkeleton from "@/modules/lead-page/new/components/sidebar/skeletons/customer-sidebar-skeleton";
import LeadHeaderSkeleton from "@/modules/lead-page/new/components/header/lead-header-skeleton";
import LeadHeader from "@/modules/lead-page/new/components/header/lead-header";
import Navbar from "@/modules/common/components/ui-kit/navbar";
import ActivityTab from "@/modules/lead-page/new/components/activity/activity-tab";
import PropertiesTab from "@/modules/lead-page/new/components/properties/properties-tab";
import SalesTab from "@/modules/lead-page/new/components/sales/sales-tab";
import NextActions from "@/modules/lead-page/new/components/task-management/next-actions";
import LeadTasks from "@/modules/lead-page/new/components/task-management/lead-tasks";
import Recommendations from "@/modules/lead-page/new/components/recommendations/recommendations";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { ActivityProvider } from "@/modules/lead-page/new/context/activity.context";
import { LeadPageTab } from "@/modules/lead-page/new/constants";
import AddComment from "../activity/add-comment";
import { ActivityScrollProvider } from "@/modules/lead-page/new/context/activity-scroll-context";
import InterestsTab from "../lead-interests";
import ReviewsTab from "../ratings";
import DeveloperTracksTab from "../developer-tracks";
import ActivityHeader from "@/modules/lead-page/new/components/activity/activity-header";
import SectionHeader from "@/modules/lead-page/new/components/common/section-header";
import useGetLeadSales from "@/modules/lead-page/hooks/get-lead-sales";
import SectionHeaderFilters from "../common/section-header-filters";
import { useRatingFilter } from "@/modules/lead-page/new/hooks/ratings/use-rating-filter.hook";
import useGetLeadTracks from "@/modules/lead-page/hooks/get-lead-listings";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import SquareButton from "@/modules/common/components/ui/square-button";
import { Plus } from "lucide-react";
import AddTrackDetailedForm from "@/modules/property-tracks/components/track-actions/add-track-detailed/form";
import {
  EnvironmentVariables,
  OrganizationTierTypes,
} from "@/modules/common/types";

interface DesktopLayoutProps {
  isLoading: boolean;
  activeTab: LeadPageTab;
  isLeadArchived: boolean;
  filteredTabs: any[];
  onTabChange: (tabId: string) => void;
  onNavigateToTrack?: (trackUId: string) => void;
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  isLoading,
  activeTab,
  isLeadArchived,
  filteredTabs,
  onTabChange,
  onNavigateToTrack,
}) => {
  const theme = useTheme();
  const { lead } = useContext(LeadPageContext);
  const [modalOpen, setModalOpen] = useState(false);
  const customerUId = lead?.data?.lead?.customer_uid;
  const leadTypeUId = lead?.data?.lead_type?.id;

  const { leadListings, count: totalPropertiesCount } = useGetLeadTracks({
    enabled: activeTab === LeadPageTab.PROPERTIES,
  });
  const { leadSales } = useGetLeadSales({
    enabled: activeTab === LeadPageTab.SALES,
  });
  const { ratingFilterHookResult, ratingValue, sortOrder } = useRatingFilter();

  // Track whether there are any ratings at all (unfiltered)
  const [hasAnyRatings, setHasAnyRatings] = useState<boolean>(false);

  // Check if user has applied a rating filter
  const isRatingFilterActive = ratingValue !== undefined;
  const hasAssignedAgent = !!lead?.data?.lead?.user?.id;

  // Map tab ids to their respective components (memoized to prevent recreating on every render)
  const tabContentMap: Record<string, JSX.Element> = useMemo(
    () => ({
      [LeadPageTab.ACTIVITY]: (
        <ActivityScrollProvider>
          <ActivityTab
            isHeaderCollapsed={false}
            onCollapseChange={undefined}
            addComment={<AddComment />}
            onNavigateToTrack={onNavigateToTrack}
          />
        </ActivityScrollProvider>
      ),
      [LeadPageTab.PROPERTIES]: (
        <PropertiesTab onAddProperty={() => setModalOpen(true)} />
      ),
      [LeadPageTab.SALES]: <SalesTab />,
      [LeadPageTab.INTERESTS]: <InterestsTab />,
      [LeadPageTab.DEVELOPER_TRACKS]: <DeveloperTracksTab />,
      [LeadPageTab.REVIEWS]: (
        <ReviewsTab
          ratingFilter={ratingValue}
          sortOrder={sortOrder}
          onHasRatingsChange={setHasAnyRatings}
          onTabChange={onTabChange}
        />
      ),
    }),
    [ratingValue, sortOrder, onTabChange]
  );

  const tabHeaderMap: Record<string, JSX.Element> = useMemo(
    () => ({
      [LeadPageTab.ACTIVITY]: <ActivityHeader />,
      [LeadPageTab.PROPERTIES]:
        leadListings.length > 0 ? (
          <SectionHeader
            title="Properties"
            count={totalPropertiesCount || leadListings.length}
            rightElement={
              !isLeadArchived &&
              customerUId && (
                <SquareButton
                  variant="ghost"
                  onClick={() => setModalOpen(true)}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: theme.nawy.colors.semantic.text.primary,
                    width: "fit-content",
                    padding: "4px 16px",
                    gap: "8px",
                  }}
                >
                  <Plus size={16} />
                  Add Property
                </SquareButton>
              )
            }
          />
        ) : (
          <></>
        ),
      [LeadPageTab.SALES]:
        leadSales.length > 0 ? (
          <SectionHeader title="Sales" count={leadSales.length} />
        ) : (
          <></>
        ),
      [LeadPageTab.REVIEWS]:
        // Show filters if there's any data OR if user has applied a filter
        hasAnyRatings || isRatingFilterActive ? (
          <SectionHeaderFilters filterHookResult={ratingFilterHookResult} />
        ) : (
          <></>
        ),
    }),
    [
      ratingFilterHookResult,
      leadListings.length,
      leadSales.length,
      hasAnyRatings,
      isRatingFilterActive,
      totalPropertiesCount,
      isLeadArchived,
      customerUId,
      theme,
    ]
  );

  // Calculate padding based on active tab (prevents recreating component tree)
  const taskManagementPadding = useMemo(
    () =>
      activeTab === LeadPageTab.ACTIVITY
        ? `${theme.nawy.spacing.sm} ${theme.nawy.spacing.sm} 0 0`
        : theme.nawy.spacing.sm,
    [activeTab, theme.nawy.spacing.sm]
  );

  // Memoized Task Management sidebar
  const taskManagementContent = useMemo(
    () => (
      <Stack
        direction="column"
        width="280px"
        gap={theme.nawy.spacing.sm}
        sx={{
          height: "100%",
          overflowY: "auto",
          flexShrink: 0,
          padding: taskManagementPadding,
        }}
      >
        {EnvironmentVariables.COMPANY_NAME.toString() ===
          OrganizationTierTypes.NAWY &&
          hasAssignedAgent && (
            <ArchivedOverlay isArchived={isLeadArchived}>
              <LeadTasks />
            </ArchivedOverlay>
          )}

        <ArchivedOverlay isArchived={isLeadArchived}>
          <NextActions />
        </ArchivedOverlay>

        <ArchivedOverlay isArchived={isLeadArchived}>
          <Recommendations />
        </ArchivedOverlay>
      </Stack>
    ),
    [
      theme.nawy.spacing.sm,
      taskManagementPadding,
      isLeadArchived,
      hasAssignedAgent,
    ]
  );

  // Check if sidebar should be hidden for certain tabs
  const shouldHideSidebar = activeTab === LeadPageTab.DEVELOPER_TRACKS;

  // Memoized content container
  const contentContainer = useMemo(
    () => (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          width: "100%",
          display: "flex",
          bgcolor: theme.nawy.colors.semantic.layer.secondary,
        }}
      >
        <Stack
          direction="row"
          width="100%"
          sx={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            width: "100%",
          }}
        >
          {/* Tab Content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              width: "100%",
              display: "flex",
              padding: shouldHideSidebar ? 0 : theme.nawy.spacing.sm,
              bgcolor: "transparent",
            }}
          >
            <Box sx={{ width: "100%", height: "100%" }}>
              {tabContentMap[activeTab]}
            </Box>
          </Box>

          {/* Task Management - Hidden for Developer Tracks tab */}
          {!shouldHideSidebar && taskManagementContent}
        </Stack>
      </Box>
    ),
    [
      theme.nawy.colors.semantic.layer.secondary,
      theme.nawy.spacing.sm,
      tabContentMap,
      activeTab,
      taskManagementContent,
      shouldHideSidebar,
    ]
  );

  // Render header and content wrapped with ActivityProvider
  const renderTabContent = useCallback(() => {
    const header = (
      <ArchivedOverlay isArchived={isLeadArchived}>
        {tabHeaderMap[activeTab]}
      </ArchivedOverlay>
    );

    // Wrap all tabs with ActivityProvider (needed for Reviews tab to access activity data)
    return (
      <ActivityProvider>
        {header}
        {contentContainer}
      </ActivityProvider>
    );
  }, [activeTab, isLeadArchived, tabHeaderMap, contentContainer]);

  return (
    <Stack direction="row" width="100%" flex={1} minHeight={0}>
      {/* Customer Sidebar */}
      <Stack
        direction="column"
        spacing={theme.nawy.spacing.md}
        width="300px"
        height="100%"
        alignItems="center"
        borderRight={`1px solid ${theme.nawy.colors.semantic.border.secondary}`}
        borderRadius={`${theme.nawy.radius["2xl"]} 0 0 0`}
        bgcolor={theme.nawy.colors.semantic.layer.primary}
        sx={{
          overflow: "hidden",
        }}
      >
        {isLoading ? <CustomerSidebarSkeleton /> : <LeadSidebar />}
      </Stack>

      {/* Main Content */}
      <Stack direction="column" flex={1}>
        {/* Lead Information */}
        {isLoading ? (
          <LeadHeaderSkeleton />
        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100vw",
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              overflow: "hidden",
            }}
          >
            <LeadHeader />
          </Box>
        )}

        {/* Tabs and Content */}
        <Stack direction="column" width="100%" sx={{ flex: 1, minHeight: 0 }}>
          <ArchivedOverlay isArchived={isLeadArchived}>
            <Navbar
              tabs={filteredTabs}
              activeTab={activeTab}
              variant="primary"
              onTabChange={onTabChange}
            />
          </ArchivedOverlay>

          {renderTabContent()}

          {activeTab === LeadPageTab.PROPERTIES && customerUId && (
            <AddTrackDetailedForm
              customerUId={customerUId}
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              leadTypeUId={leadTypeUId?.toString()}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default DesktopLayout;
